import { warStateForWire, restoreWarState, socialWeightLbs, socialVolumeLbs } from "./socialDepth";
import { LS } from './storage';
import { sameAccountSession } from './accountActions';
import { AuthToken } from './auth';
import { today, ago, uid } from './helpers';
import { IRON_RANKS } from '../data/ranks';
import { BADGE_DEFS, calcEarnedBadges } from '../data/badges';
import { applyHealthFromWire, healthSettingsForWire } from './healthWire';

// ─── Cloud Sync Engine ───
// Allow localStorage override of API URL ONLY in dev — in production this is an
// XSS pivot (attacker sets ft-api-url to their server and exfiltrates sync payloads).
const _DEV = (typeof import.meta!=="undefined" && import.meta.env && import.meta.env.DEV)
  || (typeof location!=="undefined" && /^(localhost|127\.|0\.0\.0\.0$|\[::1\])/.test(location.hostname));
export const SYNC_URL = (_DEV && LS.get("ft-api-url")) || "https://api.ironlog.space";

export const APP_VERSION="9.0";
export const SW_VERSION="ironlog-v7.1"; // Keep in sync with sw.js

export let syncTimer=null;
// Promise-based lock — atomic across concurrent push() calls. A boolean has a
// time-of-check/time-of-use race when two callers read it before either assigns.
// Fields the setup wizard collects beyond the classic users columns
const PREF_KEYS=["heightCm","sessionLength","trainingTime","equipment","lifts","diet","allergies"];
export function profilePrefs(profile){
  const out={};
  PREF_KEYS.forEach(k=>{if(profile&&profile[k]!==undefined&&profile[k]!==null)out[k]=profile[k];});
  return out;
}

let _pushPromise=null;
// Newest state asked to push while a push was already in flight (see CloudSync.push)
let _pushAgain=null;
let _suspendedToken;
let _syncEpoch=0;
const captureSession=()=>({epoch:_syncEpoch,token:LS.get("ft-session-token"),email:LS.get("ft-session-email")});
const sessionIsCurrent=session=>sameAccountSession(session,captureSession())&&session.token!==_suspendedToken&&session.epoch===_syncEpoch;
export const syncInFlight = () => _pushPromise!==null;

// Global ref for MsgBannerCtrl (set from App)
export let MsgBannerCtrl_ref = { push: () => {} };
export function setMsgBannerCtrlRef(ctrl) { MsgBannerCtrl_ref = ctrl; }

// A 401 means the server rejected the session token (expired or revoked). Clear the
// stale token so the app's local session state matches reality (SessionManager.hasSession()
// then reads false, which social screens use to show "sign in again" instead of an
// endless "Could not load / Retry"), and surface one actionable banner. Parallel social
// loads all 401 together, so the banner is rate-limited to avoid a stack of duplicates.
let _sessionExpiredNotifiedAt = 0;
export function notifySessionExpired() {
  LS.set("ft-session-token", null);
  LS.set("ft-session-expires", null);
  if (Date.now() - _sessionExpiredNotifiedAt > 30000) {
    _sessionExpiredNotifiedAt = Date.now();
    MsgBannerCtrl_ref.push({ name: "Session Expired", text: "Tap to sign in again — your data is saved locally.", type: "reauth" });
  }
}

// S5: the API enforces org feature flags server-side (403 { error: 'feature_disabled', flag }
// on /api/social, /api/league, /api/photos/upload). The tabs are already hidden by the
// flags in `me`, so this only fires when the flag changed under a screen that was still
// open. App.jsx registers a handler that shows the errorMessage copy and refreshes `me`
// (which hides the tab); it is told once per flag, so parallel loads and pollers do not
// stack toasts or retry loops.
let _featureDisabledHandler = null;
const _featureDisabledNotified = new Set();
export const setFeatureDisabledHandler = (fn) => { _featureDisabledHandler = fn; };
export function notifyFeatureDisabled(flag) {
  const key = flag || 'feature';
  if (_featureDisabledNotified.has(key)) return;
  _featureDisabledNotified.add(key);
  try { _featureDisabledHandler?.(key); } catch {}
}
// fetch() for the social/league endpoints: routes a feature_disabled 403 through the
// notifier; the response is returned unchanged so every caller keeps its `r.ok` check.
export async function socialFetch(url, init) {
  const r = await fetch(url, init);
  if (r.status === 401) notifySessionExpired();
  if (r.status === 403) {
    const body = await r.clone().json().catch(() => null);
    if (body?.error === 'feature_disabled') notifyFeatureDisabled(body.flag);
  }
  return r;
}

// ─── Offline Sync Queue (IndexedDB) ───
export const SyncQueue={
  _open:()=>new Promise((resolve,reject)=>{
    const req=indexedDB.open("ironlog-sync-queue",1);
    req.onupgradeneeded=(e)=>{const db=e.target.result;if(!db.objectStoreNames.contains("queue"))db.createObjectStore("queue",{keyPath:"id",autoIncrement:true});};
    req.onsuccess=(e)=>resolve(e.target.result);
    req.onerror=(e)=>reject(e.target.error);
  }),
  // Every queued item records WHICH ACCOUNT it belongs to. The server ignores the email
  // in the body and writes under whatever session token replays the item, and the row
  // ids (workouts.id, nutrition.id, …) are global primary keys, so an upsert with a
  // different session overwrites user_email — the previous user's history is not
  // duplicated into the new account, it is transferred to it. Replay therefore has to
  // be able to tell whose work each item is.
  add:async(url,body,session=captureSession())=>{
    try{
      if(!sessionIsCurrent(session))return;
      const db=await SyncQueue._open();
      if(!sessionIsCurrent(session))return;
      const tx=db.transaction("queue","readwrite");
      const owner=(LS.get("ft-session-email")||LS.get("ft-email")||"").toLowerCase()||null;
      // Each item is a full-state push snapshot, so only the newest is worth keeping:
      // replaying an older one would overwrite newer data, and N copies of the whole
      // history would pile up in IndexedDB while offline.
      const store=tx.objectStore("queue");
      store.clear();
      store.add({url,body,owner,timestamp:new Date().toISOString()});
      // Wait for the transaction to actually commit before reporting success / requesting bg sync
      await new Promise((res,rej)=>{tx.oncomplete=res;tx.onabort=tx.onerror=()=>rej(tx.error);});
      // Derive count from the store rather than caching a separate counter (avoids drift)
      const c = await SyncQueue.count();
      if(!sessionIsCurrent(session))return;
      LS.set("ft-pending-sync",c);
      // Request background sync if available
      if("serviceWorker" in navigator&&"SyncManager" in window){
        const reg=await navigator.serviceWorker.ready;
        await reg.sync.register("ironlog-sync").catch(()=>{});
      }
    }catch(e){console.warn("SyncQueue.add failed:",e);}
  },
  count:async()=>{
    try{const db=await SyncQueue._open();return new Promise(r=>{
      const tx=db.transaction("queue","readonly");const c=tx.objectStore("queue").count();
      c.onsuccess=()=>r(c.result);c.onerror=()=>r(0);
    });}catch(e){return 0;}
  },
  clear:async(stillCurrent=()=>true)=>{
    try{const db=await SyncQueue._open();if(!stillCurrent())return false;
      const tx=db.transaction("queue","readwrite");tx.objectStore("queue").clear();
      await new Promise((resolve,reject)=>{tx.oncomplete=resolve;tx.onabort=tx.onerror=()=>reject(tx.error);});
      if(!stillCurrent())return false;LS.set("ft-pending-sync",0);return true;
    }catch(e){console.warn("Error:",e);return false;}
  },
  processAll:async()=>{
    const session=captureSession();
    if(!sessionIsCurrent(session))return {synced:0,remaining:0};
    try{const db=await SyncQueue._open();const tx=db.transaction("queue","readonly");
    const getAll=tx.objectStore("queue").getAll();
    return new Promise(resolve=>{
      getAll.onsuccess=async()=>{
        const allItems=getAll.result||[];let synced=0;let discarded=0;
        const stale=()=>!sessionIsCurrent(session);
        if(stale()){resolve({synced:0,remaining:allItems.length});return;}
        // Read current session token at replay time so queued items pass auth
        const sessionToken=LS.get("ft-session-token");
        // …but auth alone is not enough: the token says who is signed in NOW, while the
        // item was recorded for whoever was signed in THEN. Replaying across that
        // boundary writes one person's training history into another person's account.
        // An item with no owner predates this field; it is dropped rather than guessed at.
        const headers={"Content-Type":"application/json"};
        if(sessionToken)headers["X-Session-Token"]=sessionToken;
        // Await each delete transaction's oncomplete so the on-disk queue is consistent
        // with the synced/discarded counters before we resolve.
        const deleteQueued=(id)=>new Promise((res,rej)=>{
          const delTx=db.transaction("queue","readwrite");
          delTx.objectStore("queue").delete(id);
          delTx.oncomplete=()=>res();
          delTx.onabort=delTx.onerror=()=>rej(delTx.error);
        });
        const account=(LS.get("ft-session-email")||LS.get("ft-email")||"").toLowerCase();
        const items=[];
        for(const it of allItems){
          if(stale()){resolve({synced,remaining:allItems.length});return;}
          if(account&&it.owner&&it.owner===account){items.push(it);continue;}
          try{await deleteQueued(it.id);}catch(e){/* fall through; counted as discarded */}
          discarded++;
        }
        for(const item of items){
          if(stale()){resolve({synced,remaining:items.length-synced});return;}
          try{const res=await fetch(item.url,{method:"POST",headers,body:item.body});
          if(stale()){resolve({synced,remaining:items.length-synced});return;}
          if(res.ok){await deleteQueued(item.id);synced++;}
          else if(res.status===401){resolve({synced,remaining:items.length-synced-discarded,authError:true});return;}
          // #12: Drop permanently-invalid items (400/403/404/422) — previously left in queue causing infinite retry.
          // A 5xx or network error is transient so we leave those for next sync.
          else if(res.status===400||res.status===403||res.status===404||res.status===409||res.status===422){
            await deleteQueued(item.id);
            discarded++;
            console.warn("SyncQueue: discarding permanently-failed item",item.id,"status",res.status);
          }}catch(e){console.warn("Error:",e);}
        }
        const remaining=Math.max(0,items.length-synced-discarded);
        if(!stale())LS.set("ft-pending-sync",remaining);
        resolve({synced,remaining});
      };
      getAll.onerror=()=>resolve({synced:0,remaining:0});
    });}catch(e){return{synced:0,remaining:0};}
  }
};
export const ActiveWorkoutStore={
  _open:()=>new Promise((res,rej)=>{
    const r=indexedDB.open("ironlog-active-workout",1);
    r.onupgradeneeded=(e)=>{const db=e.target.result;if(!db.objectStoreNames.contains("active"))db.createObjectStore("active",{keyPath:"id"});};
    r.onsuccess=(e)=>res(e.target.result);r.onerror=(e)=>rej(e.target.error);
  }),
  save:async(workout)=>{
    const session=captureSession();
    try{const db=await ActiveWorkoutStore._open();if(session.epoch!==_syncEpoch||session.token===_suspendedToken||session.token!==LS.get("ft-session-token")||session.email!==LS.get("ft-session-email"))return;const tx=db.transaction("active","readwrite");tx.objectStore("active").put({id:"current",...workout,savedAt:new Date().toISOString()});}catch(e){}
  },
  load:async()=>{
    try{const db=await ActiveWorkoutStore._open();return new Promise(r=>{const tx=db.transaction("active","readonly");const req=tx.objectStore("active").get("current");req.onsuccess=()=>r(req.result||null);req.onerror=()=>r(null);});}catch(e){return null;}
  },
  clear:async(stillCurrent=()=>true)=>{
    try{const db=await ActiveWorkoutStore._open();if(!stillCurrent())return false;
      const tx=db.transaction("active","readwrite");tx.objectStore("active").delete("current");
      await new Promise((resolve,reject)=>{tx.oncomplete=resolve;tx.onabort=tx.onerror=()=>reject(tx.error);});
      return stillCurrent();
    }catch(e){return false;}
  },
};
export const ReconcileAPI = {
  _key: 'ft-reconcile-result',
  _tsKey: 'ft-reconcile-ts',
  // Minimum gap between auto-runs (4 hours)
  _intervalMs: 4 * 60 * 60 * 1000,

  async run(state) {
    const email = state?.profile?.email;
    if (!email) return null;
    const clientCounts = {
      workouts: state.workouts?.length ?? 0,
      nutrition: state.nutrition?.length ?? 0,
      body: state.body?.length ?? 0,
      photos: state.photos?.length ?? 0,
    };
    try {
      const res = await fetch(`${SYNC_URL}/api/sync/reconcile`, {
        method: 'POST',
        headers: AuthToken.getHeaders(email),
        body: JSON.stringify({
          email,
          deviceId: LS.get('ft-device-id') || 'unknown',
          clientCounts,
        }),
      });
      if (res.status === 401) return { status: 'auth_error', ran_at: new Date().toISOString() };
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const result = { ...data, ran_at: new Date().toISOString() };
      LS.set(ReconcileAPI._key, result);
      LS.set(ReconcileAPI._tsKey, Date.now());
      return result;
    } catch(e) {
      const err = { status: 'error', error: e.message, ran_at: new Date().toISOString() };
      LS.set(ReconcileAPI._key, err);
      return err;
    }
  },

  // Returns the last stored result (no network call)
  last() { return LS.get(ReconcileAPI._key); },

  // Returns true if enough time has passed to warrant an auto-run
  shouldAutoRun() {
    const last = parseInt(LS.get(ReconcileAPI._tsKey) || '0');
    return Date.now() - last > ReconcileAPI._intervalMs;
  },
};
export const SocialAPI={
  _headers:(email)=>({...AuthToken.getHeaders(email)}),

  // Friends
  getFriends:async(email)=>{
    // #B5: email removed from GET query string — server reads identity from session token.
    // Sending email in the URL logged it to Vercel/CDN/proxy access logs unnecessarily.
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=friends`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();console.warn('getFriends failed:',r.status);return null;}return await r.json();}catch(e){console.warn('getFriends error:',e.message);return null;}
  },
  sendRequest:async(email,target)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=friends`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"friends",email,action:"send",target})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  acceptRequest:async(email,from)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=friends`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"friends",email,action:"accept",target:from})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  removeFriend:async(email,target)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=friends`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"friends",email,action:"remove",target})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  blockUser:async(email,target)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=friends`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"friends",email,action:"block",target})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  unblockUser:async(email,target)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=friends`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"friends",email,action:"unblock",target})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  // People you may know: mutual friends, same city/state, gym-partner opt-in
  getSuggestions:async(email)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=suggestions`,{headers:SocialAPI._headers(email)});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Ranked duels — scored and declared by the server
  getDuels:async(email)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=duels`,{headers:SocialAPI._headers(email)});return r.ok?await r.json():null;}catch(e){return null;}
  },
  duelAction:async(email,action,body)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=duels`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"duels",email,action,...body})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Weekly XP league
  leagueSync:async(email,week,xp,name)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/league`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({action:"sync",week,xp,name})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Feed
  getFeed:async(email,limit)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=feed&limit=${limit||30}`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();console.warn('getFeed failed:',r.status);return null;}return await r.json();}catch(e){console.warn('getFeed error:',e.message);return null;}
  },
  logEvent:async(email,type,data,visibility)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=feed`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"feed",email,action:"log_event",type,data,visibility:visibility||"friends"})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  react:async(email,eventId,reaction)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=feed`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"feed",email,action:"react",event_id:eventId,reaction})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Groups
  getGroups:async(email)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();console.warn('getGroups failed:',r.status);return null;}return await r.json();}catch(e){console.warn('getGroups error:',e.message);return null;}
  },
  createGroup:async(email,name,desc)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"groups",email,action:"create",name,description:desc})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  joinGroup:async(email,code)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"groups",email,action:"join",code})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  leaveGroup:async(email,code)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"groups",email,action:"leave",code})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  getMembers:async(email,code)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"groups",email,action:"members",code})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  getGroupEvents:async(email,code,type)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=group_events&group=${encodeURIComponent(code)}&type=${encodeURIComponent(type||"")}`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();return null;}return await r.json();}catch(e){return null;}
  },
  // Direct Messages
  getDMs:async(email,friendEmail,before,limit)=>{
    try{
      const params=new URLSearchParams({route:"dms",friend:friendEmail});
      if(before)params.set("before",before);
      if(limit)params.set("limit",String(limit));
      const r=await socialFetch(`${SYNC_URL}/api/social?${params}`,{headers:SocialAPI._headers(email)});
      if(!r.ok){if(r.status===401)notifySessionExpired();return null;}
      return await r.json();
    }catch(e){return null;}
  },
  sendDM:async(email,friendEmail,text,name)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=feed`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"feed",email,action:"log_event",type:"DirectMessage",data:{to:friendEmail,text,name},visibility:"private"})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  markDMsRead:async(email,friendEmail,readAt=new Date().toISOString())=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=feed`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"feed",email,action:"log_event",type:"DMRead",data:{partner:friendEmail,readAt},visibility:"private"})});return r.ok;}catch(e){return false;}
  },
  checkMessages:async(email,since)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=check_messages&since=${encodeURIComponent(since)}`,{headers:SocialAPI._headers(email)});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Notifications
  getNotifications:async(email)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=notifications`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();return null;}return await r.json();}catch(e){return null;}
  },
  markRead:async(email,id)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"notifications",action:"notifications",email,mark_read:id||"all"})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  deleteNotif:async(email,id)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"notifications",email,delete_id:id})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  clearReadNotifs:async(email)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"notifications",email,clear_all:true})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Comments on feed events
  getComments:async(email,eventId)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=comments&event_id=${encodeURIComponent(eventId)}`,{headers:SocialAPI._headers(email)});return r.ok?await r.json():null;}catch(e){return null;}
  },
  addComment:async(email,eventId,text)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"comments",email,action:"add",event_id:eventId,text})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  deleteComment:async(email,commentId)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"comments",email,action:"delete",comment_id:commentId})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Challenge snapshots
  snapshotChallenges:async(email,challenges,badges)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"snapshot",email,challenges,badges})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Username
  setUsername:async(email,username)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"set_username",email,username})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Sync profile avatar + banner to server so friends can see them
  updateProfile:async(email,{avatar,banner,bio,username,privacy})=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"update_profile",email,avatar,banner,bio,username,privacy})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Trigger a push notification to a DM recipient (server sends web push if they have it enabled)
  notifyDM:async(email,friendEmail,name,text,imageMsg)=>{
    try{await fetch(`${SYNC_URL}/api/push`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({action:"notify_dm",from:email,to:friendEmail,name,text:imageMsg?"📷 Photo":text})});}catch(e){}
  },

  // Send a DM with optional image attachment
  sendDMImage:async(email,friendEmail,imageData,name)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=feed`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"feed",email,action:"log_event",type:"DirectMessage",data:{to:friendEmail,text:"📷 Photo",name,imageData},visibility:"private"})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Profile lookup
  getProfile:async(username)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=profile&username=${encodeURIComponent(username)}`);return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Global discovery
  discoverUsers:async(email,q,page,partners)=>{
    try{const params=new URLSearchParams({route:"discover_users"});if(q)params.set("q",q);if(page)params.set("page",String(page));if(partners)params.set("partners","1");const r=await socialFetch(`${SYNC_URL}/api/social?${params}`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();console.warn('discoverUsers failed:',r.status);return null;}return await r.json();}catch(e){console.warn('discoverUsers error:',e.message);return null;}
  },
  discoverGroups:async(email,q,page)=>{
    try{const params=new URLSearchParams({route:"discover_groups"});if(q)params.set("q",q);if(page)params.set("page",String(page));const r=await socialFetch(`${SYNC_URL}/api/social?${params}`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();console.warn('discoverGroups failed:',r.status);return null;}return await r.json();}catch(e){console.warn('discoverGroups error:',e.message);return null;}
  },
  globalLeaderboard:async(email,metric)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=global_leaderboard&metric=${encodeURIComponent(metric||"streak")}`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();console.warn('globalLeaderboard failed:',r.status);return null;}return await r.json();}catch(e){console.warn('globalLeaderboard error:',e.message);return null;}
  },

  // Message management
  deleteMessage:async(email,eventId)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=feed`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"feed",email,action:"delete_message",event_id:eventId})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Group management
  setGroupRole:async(email,code,targetEmail,role)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"groups",email,action:"set_role",code,target_email:targetEmail,role})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  kickMember:async(email,code,targetEmail)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"groups",email,action:"kick",code,target_email:targetEmail})});return r.ok?await r.json():null;}catch(e){return null;}
  },
  pinMessage:async(email,code,text)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=groups`,{method:"POST",headers:SocialAPI._headers(email),body:JSON.stringify({route:"groups",email,action:"pin_message",code,text})});return r.ok?await r.json():null;}catch(e){return null;}
  },

  // Friend profile extras
  getFriendWorkouts:async(email,friendEmail)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=friend_workouts&friend=${encodeURIComponent(friendEmail)}`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();return null;}return await r.json();}catch(e){return null;}
  },

  // Group wars
  getGroupWar:async(email,groupCode,metric)=>{
    try{const r=await socialFetch(`${SYNC_URL}/api/social?route=group_war&group=${encodeURIComponent(groupCode)}&metric=${encodeURIComponent(metric||"weekWorkouts")}`,{headers:SocialAPI._headers(email)});if(!r.ok){if(r.status===401)notifySessionExpired();return null;}return await r.json();}catch(e){return null;}
  },
};
export const CloudSync={
  suspend:()=>{_syncEpoch++;_suspendedToken=LS.get("ft-session-token");clearTimeout(syncTimer);syncTimer=null;},
  resume:()=>{_syncEpoch++;_suspendedToken=undefined;},
  // Set by the app once the reducer exists, so a confirmed delete can clear its
  // tombstones. Left undefined in tests and before boot; the call site is optional.
  _onDeletionsApplied: null,
  // Push all data to server (debounced, silent) — queues on failure
  push:async(state)=>{
    // Atomic lock — returns the in-flight promise so concurrent callers await the same op.
    // The payload was built before this call, so remember the newer state (newest wins) and
    // push it once the current request finishes; otherwise edits made mid-flight never sent.
    if(_pushPromise){_pushAgain=state;return _pushPromise;}
    const pushEmail=(st)=>{
      const email=st?.profile?.email;const cur=captureSession();
      return email&&sessionIsCurrent(cur)&&email.toLowerCase()===cur.email?.toLowerCase()?email:null;
    };
    if(!pushEmail(state))return;
    // The lock is released in the same tick as the last _pushAgain check, so a push asked
    // for in between can never be dropped. The first pass always awaits (checked above).
    _pushPromise = (async()=>{
      try{
        for(let next=state;next;next=_pushAgain){
          _pushAgain=null;
          const email=pushEmail(next);
          if(email)await CloudSync._doPush(next, email);
        }
      }finally{ _pushPromise=null; _pushAgain=null; }
    })();
    return _pushPromise;
  },
  _doPush:async(state,email)=>{
    const session=captureSession();
    if(!sessionIsCurrent(session))return;
    // Surface photos dropped by the 500KB cap so users aren't silently losing them
    const allPhotos=state.photos||[];
    const photosOK=allPhotos.filter(p=>!p.data||p.data.length<500000);
    const droppedPhotos=allPhotos.length - photosOK.length;
    if(droppedPhotos>0 && (state.syncPrefs||{}).photos!==false){
      try{
        const lastWarned=parseInt(LS.get("ft-photo-drop-warned")||"0");
        if(Date.now()-lastWarned > 24*60*60*1000){
          MsgBannerCtrl_ref.push({name:"Photos too large",text:`${droppedPhotos} photo${droppedPhotos>1?"s":""} skipped (>500KB). Re-take or compress to sync.`,type:"system"});
          LS.set("ft-photo-drop-warned",Date.now());
        }
      }catch{}
    }
    // Exactly the tombstones in this payload: only these may be forgotten on success, not
    // ids deleted while the request was in flight.
    const sentDeletions=state.deletions||undefined;
    const payload=JSON.stringify({
      email,
      deviceId:LS.get("ft-device-id")||"unknown",
      appVersion:APP_VERSION,
      workouts:(state.syncPrefs||{}).workouts!==false?state.workouts||[]:[],
      nutrition:(state.syncPrefs||{}).nutrition!==false?state.nutrition||[]:[],
      body:(state.syncPrefs||{}).body!==false?state.body||[]:[],
      photos:(state.syncPrefs||{}).photos!==false?photosOK:[],
      checkins:(state.syncPrefs||{}).checkins!==false?state.checkins||[]:[],
      milestones:(state.syncPrefs||{}).milestones!==false?state.milestones||[]:[],
      // Ids this device deleted. Sent explicitly because a push legitimately omits rows
      // for other reasons — a disabled syncPref, an oversized photo — so the server must
      // never infer deletion from absence.
      deletions:sentDeletions,
      settings:{goals:state.goals,schedule:state.schedule,activeProgram:state.activeProgram??null,exercises:state.exercises,units:state.units,
        // Onboarding preferences live on the users row (jsonb) so they survive a reinstall
        profilePrefs:profilePrefs(state.profile),
        // vault_pin omitted — kept local-only, never sent to server
        ...healthSettingsForWire(state),
        privacy:LS.get("ft-privacy")||{},
        accountability:LS.get("ft-accountability")||[],
        // Gamification — all local-only keys bundled here so they survive device switches
        gamification:{
          xp_bonus:         LS.get("ft-xp-bonus")||{total:0,log:[]},
          missions_completed:LS.get("ft-missions-completed")||{},
          streak_shields:    LS.get("ft-streak-shields")||0,
          shield_awarded_streak: LS.get("ft-shield-awarded-streak")||0,
          shield_used_date:  LS.get("ft-shield-used-date")||null,
          last_known_level:  LS.get("ft-last-known-level")||1,
          badge_dates:       LS.get("ft-badge-dates")||{},
          duels:             LS.get("ft-duels")||[],
          rivals:            LS.get("ft-rivals")||[],
          ...warStateForWire(LS),
          war_wins:          LS.get("ft-war-wins")||0,
          war_streak:        LS.get("ft-war-streak")||0,
          comeback_used:     LS.get("ft-comeback-used")||null,
          duel_record:       LS.get("ft-duel-record")||{wins:0,losses:0,ties:0},
        }},
    });
    try{
      const res=await fetch(`${SYNC_URL}/api/sync/push`,{method:"POST",headers:AuthToken.getHeaders(email),body:payload});
      if(!sessionIsCurrent(session))return;
      if(res.status===401){
        // I10: Session expired — clear the dead token and show an actionable banner
        // (tap → re-auth PIN prompt) instead of silently queueing.
        notifySessionExpired();
        return;
      }
      if(!res.ok)throw new Error("Push failed");
      const json=await res.json();
      if(!sessionIsCurrent(session))return;
      LS.set("ft-last-sync",new Date().toISOString());
      LS.set("ft-pending-sync",0);
      // Only forget the tombstones once the server says it applied them. If that half
      // failed, they stay and go again on the next push rather than being dropped —
      // otherwise a deleted row would quietly stay live on the server forever.
      if(json.deletions_applied&&sentDeletions)CloudSync._onDeletionsApplied?.(sentDeletions);
      // Check for app update
      if(json.latest_version&&json.latest_version!==APP_VERSION){
        LS.set("ft-update-available",json.latest_version);
        LS.set("ft-update-notes",json.update_notes||"");
      }
      // Snapshot challenge progress on every sync (non-blocking)
      const streak2=(()=>{let c=0;for(let i=0;i<60;i++){if(state.workouts.some(w=>w.date===ago(i)))c++;else if(i>0)break;}return c;})();
      const getBest=(id)=>{let b=0;(state.workouts||[]).forEach(w=>(w.exercises||[]).forEach(e=>{if(e.exerciseId===id)(e.sets||[]).forEach(st=>{const wt=parseFloat(st.weight)||0;if(wt>b)b=wt;});}));return socialWeightLbs(b,state.units);};
      const big3=getBest("bench")+getBest("squat")+getBest("deadlift");
      const protDays2=state.nutrition.filter(n=>n.date>=ago(7)&&(n.protein||0)>=(state.goals?.protein||180)).length;
      const tierCalc=(v,tiers)=>{for(let i=tiers.length-1;i>=0;i--)if(v>=tiers[i][0])return tiers[i][1];return null;};
      // Build badges using BADGE_DEFS (if available) otherwise fallback
      const badges2=[];
      if(typeof BADGE_DEFS!=="undefined"&&typeof calcEarnedBadges!=="undefined"){
        const {checks}=calcEarnedBadges(state);
        BADGE_DEFS.filter(b=>checks[b.id]).forEach(b=>badges2.push(b.name));
      } else {
        if(streak2>=7)badges2.push("On Fire");if(streak2>=30)badges2.push("Diamond Streak");
        if(big3>=1000)badges2.push("1000lb Club");if(state.workouts.length>=100)badges2.push("Century Club");
        if(protDays2>=7)badges2.push("Macro Master");
      }
      const weekWorkouts=state.workouts.filter(w=>w.date>=ago(7)).length;
      const weekVol=socialVolumeLbs(state.workouts.filter(w=>w.date>=ago(7)).reduce((a,w)=>a+(w.exercises||[]).reduce((b2,e)=>b2+(e.sets||[]).reduce((c,st)=>c+(parseFloat(st.weight)||0)*(parseInt(st.reps)||0),0),0),0),state.units);
      const duelWins=(LS.get("ft-duel-record")||{}).wins||0;
      SocialAPI.snapshotChallenges(email,[
        {id:"streak",value:streak2,tier:tierCalc(streak2,[[7,"bronze"],[14,"silver"],[21,"gold"],[30,"diamond"]])},
        {id:"big3",value:big3,tier:tierCalc(big3,[[600,"bronze"],[800,"silver"],[1000,"gold"],[1200,"diamond"]]),metadata:{bench:getBest("bench"),squat:getBest("squat"),deadlift:getBest("deadlift")}},
        {id:"macro",value:protDays2,tier:tierCalc(protDays2,[[3,"bronze"],[5,"silver"],[7,"gold"]])},
        {id:"ironscore",value:LS.get("ft-ironscore")||0,tier:(()=>{const xp2=LS.get("ft-ironscore")||0;return([...IRON_RANKS].reverse().find(r=>xp2>=r.xpNeeded)||IRON_RANKS[0]).name;})()},
        {id:"weekWorkouts",value:weekWorkouts},
        {id:"weekVol",value:weekVol},
        {id:"duel_wins",value:duelWins},
        {id:"war_wins",value:LS.get("ft-war-wins")||0},
        {id:"war_streak",value:LS.get("ft-war-streak")||0},
      ],badges2).catch(()=>{});
      // This push carried the full current state, so anything still queued is an older
      // snapshot of the same data. Drop it — replaying it would overwrite what was just sent.
      SyncQueue.clear(()=>sessionIsCurrent(session));
      // Auto-reconcile every 4 hours — non-blocking, silent
      if(ReconcileAPI.shouldAutoRun()) ReconcileAPI.run(state).catch(()=>{});
    }catch(e){
      // Queue for background sync
      if(sessionIsCurrent(session))await SyncQueue.add(`${SYNC_URL}/api/sync/push`,payload,session);
    }
  },

  // debouncedPush accepts either a state object OR a getter — using a getter
  // avoids capturing a stale snapshot when the user keeps editing during the 3s wait.
  debouncedPush:(stateOrGetter)=>{
    if(syncTimer)clearTimeout(syncTimer);
    syncTimer=setTimeout(()=>{
      const s = typeof stateOrGetter==="function" ? stateOrGetter() : stateOrGetter;
      if(s) CloudSync.push(s);
    },3000);
  },

  // Pull all data from server (for restore) — requires PIN if account has one
  pull:async(email,deviceId,pin)=>{
    const sessionToken = LS.get("ft-session-token");
    const sessionEmail = LS.get("ft-session-email");
    const localAccount = String(LS.get("ft-profile")?.email || "").trim().toLowerCase();
    try{
      const ctrl=new AbortController();
      const timer=setTimeout(()=>ctrl.abort(),15000);
      const res=await fetch(`${SYNC_URL}/api/sync/pull`,{
        method:"POST",
        headers:AuthToken.getHeaders(email),
        body:JSON.stringify({email,deviceId,pin:pin||null}),
        signal:ctrl.signal,
      });
      clearTimeout(timer);
      const json=await res.json();
      if(res.status===403&&json.pin_required)return{pin_required:true};
      if(res.status===403)return{wrong_pin:true,attempts_remaining:json.attempts_remaining,locked:json.locked};
      if(res.status===429)return{locked:true,retry_minutes:json.retry_minutes};
      // Handle 200 with success:false (account not found)
      if(json.success===false)return{error:json.error||"Account not found"};
      if(!res.ok)return{error:"Server error"};
      if(json.success){
        // Normalize injuries/phases/supplements (and replay taken-dates into
        // ft-supps-YYYY-MM-DD) so every pull path — SSO boot, sign-in restore,
        // Settings restore — sees the canonical PWA form.
        if (sessionToken !== LS.get("ft-session-token") || sessionEmail !== LS.get("ft-session-email")) {
          return { error: "Your account changed while restoring. Please try again." };
        }
        if(json.data?.gamification)restoreWarState(LS,json.data.gamification,{
          mergeLocal: !!localAccount && localAccount === String(email).trim().toLowerCase(),
        });
        if(json.data)applyHealthFromWire(json.data, {
          storage: LS,
          migrateLegacyHistory: !!localAccount && localAccount === String(email).trim().toLowerCase()
            && localAccount === String(LS.get("ft-profile")?.email || "").trim().toLowerCase(),
        });
        return json;
      }
      return null;
    }catch(e){
      if(e.name==="AbortError")return{error:"Request timed out. Please try again."};
      return{error:"Could not reach the server. Please try again later."};
    }
  },
};

// Generate and persist a device ID
if(!LS.get("ft-device-id")){
  LS.set("ft-device-id","dev_"+Math.random().toString(36).slice(2,10)+Date.now().toString(36));
}
